import React from 'react';
import { T } from './customers.theme';
import {
  ChevronIcon,
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from './CustomersUI';

/**
 * Ku Dar Macaamiil — and the same form again for editing one.
 *
 * Split exactly as the phone splits it: name, phone and monthly fee up front,
 * everything else folded behind "Xog dheeraad ah". Most customers are added
 * mid-service with a name and a number, and asking for seven fields at that
 * moment is how you end up with none of them filled.
 *
 * When editing, the extra section opens by itself if any of its fields already
 * carry a value — otherwise the person would have to go looking for the
 * address they came here to change.
 */

const BLANK = {
  name: '',
  phone: '',
  monthlyFee: '',
  district: '',
  whatsAppNumber: '',
  damiinName: '',
  damiinPhone: '',
  limit: '',
};

const EXTRA_FIELDS = ['district', 'whatsAppNumber', 'damiinName', 'damiinPhone', 'limit'];

export default function CustomerFormDialog({ customer, onClose, onSubmit }) {
  const editing = Boolean(customer);

  const [values, setValues] = React.useState(() => {
    if (!customer) return { ...BLANK };
    return {
      name: customer.name || '',
      phone: customer.phone || '',
      monthlyFee: customer.monthlyFee ?? '',
      district: customer.district || '',
      whatsAppNumber: customer.whatsAppNumber || '',
      damiinName: customer.damiinName || '',
      damiinPhone: customer.damiinPhone || '',
      limit: customer.limit ?? '',
    };
  });

  const [expanded, setExpanded] = React.useState(() =>
    Boolean(customer && EXTRA_FIELDS.some((field) => customer[field]))
  );
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const set = (field) => (value) => {
    setValues((current) => ({ ...current, [field]: value }));
    setError('');
  };

  const submit = async () => {
    if (busy) return;
    if (!values.name.trim()) {
      setError('Name is required');
      return;
    }

    setBusy(true);
    setError('');

    try {
      // Blank optional fields are dropped rather than sent as "" — an empty
      // string for `limit` is not the same as having no limit, and the server
      // would try to cast it to a number.
      const payload = { name: values.name.trim() };
      ['phone', 'district', 'whatsAppNumber', 'damiinName', 'damiinPhone'].forEach((field) => {
        if (String(values[field]).trim()) payload[field] = String(values[field]).trim();
      });
      ['monthlyFee', 'limit'].forEach((field) => {
        if (String(values[field]).trim()) payload[field] = Number(values[field]);
      });

      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save. Try again.');
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={460}>
      <div style={{ padding: '24px 26px 26px' }}>
        <DialogHeader
          title={editing ? 'Update Customer Info' : 'Ku Dar Macamiil'}
          onClose={onClose}
          locked={busy}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 18 }}>
          <PillField
            label="Name"
            name="customer-name"
            value={values.name}
            onChange={set('name')}
            onEnter={submit}
            placeholder="Magaca"
            disabled={busy}
            invalid={Boolean(error) && !values.name.trim()}
            autoFocus
          />

          <PillField
            label="Phone"
            name="customer-phone"
            value={values.phone}
            onChange={set('phone')}
            onEnter={submit}
            placeholder="Number-ka"
            disabled={busy}
          />

          <PillField
            label="Lacagta Billaha"
            name="customer-monthly-fee"
            type="number"
            value={values.monthlyFee}
            onChange={set('monthlyFee')}
            onEnter={submit}
            placeholder="Lacagta Billaha"
            disabled={busy}
          />

          <button
            type="button"
            onClick={() => setExpanded((on) => !on)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: 0,
              border: 'none',
              background: 'transparent',
              font: 'inherit',
              fontSize: 14,
              fontWeight: 600,
              color: T.brand,
              cursor: 'pointer',
              alignSelf: 'flex-start',
            }}
          >
            <ChevronIcon up={expanded} />
            <span>Xog dheeraad ah</span>
          </button>

          {expanded ? (
            <React.Fragment>
              <PillField
                label="Address"
                name="customer-district"
                value={values.district}
                onChange={set('district')}
                onEnter={submit}
                placeholder="Deegaanka"
                disabled={busy}
              />
              <PillField
                label="Whatsapp"
                name="customer-whatsapp"
                value={values.whatsAppNumber}
                onChange={set('whatsAppNumber')}
                onEnter={submit}
                placeholder="Whatsapp Number"
                disabled={busy}
              />
              <PillField
                label="Damiin"
                name="customer-damiin-name"
                value={values.damiinName}
                onChange={set('damiinName')}
                onEnter={submit}
                placeholder="Magaca Damiin-ka"
                disabled={busy}
              />
              <PillField
                label="Damiin Phone"
                name="customer-damiin-phone"
                value={values.damiinPhone}
                onChange={set('damiinPhone')}
                onEnter={submit}
                placeholder="Number-ka Damiin-ka"
                disabled={busy}
              />
              <PillField
                label="Limit"
                name="customer-limit"
                type="number"
                value={values.limit}
                onChange={set('limit')}
                onEnter={submit}
                placeholder="Limit-ka"
                disabled={busy}
              />
            </React.Fragment>
          ) : null}
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 22 }}
        >
          {editing ? 'Update Customer' : 'Ku Dar Macaamiil'}
        </PrimaryButton>
      </div>
    </Dialog>
  );
}
